import { useEffect, useState } from "react";
import {
  useWorkspaceStore,
  wsReadText,
  wsWriteText,
  wsRemove,
  fsSupported,
} from "../store/workspaceStore";
import { useCalculatorStore } from "../store/calculatorStore";
import { track } from "../lib/track";

const SESSION_EXT = ".quanta";

// Session files are `.quanta`; also recognise the older `.quanta.json` name.
function isSessionFile(name: string): boolean {
  return name.endsWith(SESSION_EXT) || name.endsWith(".quanta.json");
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Turn a session name into a safe file stem for `<stem>.quanta.json`.
function toFileStem(name: string): string {
  const stem = name.trim().replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "");
  return stem || "session";
}

export function WorkspacePanel() {
  const { name, permission, files, busy, open, reconnect, close, refresh } =
    useWorkspaceStore();
  const [selected, setSelected] = useState<string | null>(null);
  const [content, setContent] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  // A folder was picked before but access needs re-granting after a reload.
  const needsReconnect = fsSupported && !!name && permission !== "granted";
  const hasFolder = permission === "granted";
  // We can always browse (a real folder, or the localStorage-backed store)
  // unless a picked folder is pending reconnection.
  const canBrowse = !needsReconnect;

  useEffect(() => {
    if (canBrowse) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBrowse, hasFolder]);

  const openFile = async (fname: string) => {
    setSelected(fname);
    setContent((await wsReadText(fname)) ?? "");
  };

  const download = async (fname: string) => {
    const text = (await wsReadText(fname)) ?? "";
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    track("file-exported", fname.split(".").pop() || "");
  };

  const importFiles = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async () => {
      const list = Array.from(input.files || []);
      for (const f of list) await wsWriteText(f.name, await f.text());
      if (list.length) {
        track("file-imported", "", list.length);
        setStatus(`Imported ${list.length} file(s)`);
      }
      await refresh();
    };
    input.click();
  };

  const newFile = async () => {
    const fname = window.prompt("New file name (e.g. notes.txt, data.json):");
    if (!fname) return;
    await wsWriteText(fname, "");
    track("file-created", fname.split(".").pop() || "");
    await refresh();
    void openFile(fname);
  };

  // Open one local file from disk into the workspace and preview it.
  const openLocalFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const text = await f.text();
      await wsWriteText(f.name, text);
      track("file-opened-local", f.name.split(".").pop() || "");
      await refresh();
      setSelected(f.name);
      setContent(text);
      setStatus(`Opened ${f.name}`);
    };
    input.click();
  };

  // Save the active calculator session to the workspace as `<name>.quanta.json`.
  const saveSession = async () => {
    const store = useCalculatorStore.getState();
    const session = store.getActiveSession();
    if (!session) return;
    const data = store.exportSessionData(session.id);
    if (!data) return;
    const fname = `${toFileStem(session.name)}${SESSION_EXT}`;
    await wsWriteText(fname, JSON.stringify(data, null, 2));
    track("session-saved");
    setStatus(`Saved session to ${fname}`);
    await refresh();
  };

  // Load a `*.quanta.json` file as a brand-new calculator session.
  const openAsSession = async (fname: string) => {
    const text = await wsReadText(fname);
    if (text === null) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setStatus(`${fname} is not a valid session file`);
      return;
    }
    const newId = useCalculatorStore.getState().importSessionData(parsed);
    if (!newId) {
      setStatus(`${fname} is not a Quanta session file`);
      return;
    }
    track("session-opened");
    useCalculatorStore.getState().setActiveTab("calculator");
  };

  const saveOpen = async () => {
    if (!selected) return;
    await wsWriteText(selected, content);
    setStatus(`Saved ${selected}`);
    await refresh();
  };

  const del = async (fname: string) => {
    if (!window.confirm(`Delete ${fname}?`)) return;
    await wsRemove(fname);
    track("file-deleted");
    if (selected === fname) {
      setSelected(null);
      setContent("");
    }
    await refresh();
  };

  const openWorkspace = async () => {
    await open();
    if (useWorkspaceStore.getState().permission === "granted")
      track("workspace-opened");
  };

  return (
    <div className="marketplace-panel">
      <div className="marketplace-header">
        <h2>Workspace</h2>
      </div>

      {/* Connection / folder controls */}
      <div style={{ marginBottom: 16 }}>
        {hasFolder ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>
              Folder on disk: <strong>{name}</strong>
            </span>
            <button className="btn-secondary" onClick={openWorkspace}>
              Change folder
            </button>
            <button className="btn-secondary" onClick={() => void close()}>
              Close
            </button>
          </div>
        ) : needsReconnect ? (
          <div>
            <p style={{ color: "var(--text-secondary)" }}>
              Reconnect your workspace folder <strong>{name}</strong> to grant
              access again (needed once after a reload).
            </p>
            <button className="btn-primary" onClick={() => void reconnect()}>
              Reconnect “{name}”
            </button>
          </div>
        ) : fsSupported ? (
          <div>
            <p style={{ color: "var(--text-secondary)" }}>
              Files are currently stored in your browser. <strong>Open a folder</strong>{" "}
              on your computer to save them as real files on disk, so anything you
              write with <code>save</code> or <code>csvwrite</code> can be opened
              in other apps and shared.
            </p>
            <button className="btn-primary" onClick={openWorkspace}>
              Open workspace folder
            </button>
          </div>
        ) : (
          <p style={{ color: "var(--text-secondary)" }}>
            This browser can’t pick a local folder, so your workspace is stored in
            the browser. Use <strong>Import</strong> and <strong>Download</strong>{" "}
            below to move files in and out for sharing. (For a folder on disk, use
            Chrome or Edge.)
          </p>
        )}
      </div>

      {canBrowse && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <button className="btn-secondary" onClick={newFile}>
              New file
            </button>
            <button className="btn-secondary" onClick={openLocalFile}>
              Open local file…
            </button>
            <button className="btn-secondary" onClick={importFiles}>
              Import…
            </button>
            <button className="btn-secondary" onClick={() => void saveSession()}>
              Save session
            </button>
            <button className="btn-secondary" onClick={() => void refresh()}>
              Refresh
            </button>
            {status && (
              <span style={{ color: "var(--text-secondary)", alignSelf: "center" }}>
                {status}
              </span>
            )}
          </div>

          <p style={{ color: "var(--text-secondary)", fontSize: "0.85em", marginBottom: 12 }}>
            <strong>Save session</strong> writes the current session (its inputs and
            results) to a <code>{SESSION_EXT}</code> file you can share. Click{" "}
            <strong>⇱</strong> on any such file to open it as a new session.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) 2fr", gap: 16 }}>
            <div>
              <h3>Files ({files.length})</h3>
              {files.length === 0 ? (
                <p style={{ color: "var(--text-secondary)", fontSize: "0.9em" }}>
                  No files yet. Create one, import one, or run{" "}
                  <code>save('data.json', 'x')</code> in the calculator.
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {files.map((f) => (
                    <div
                      key={f.name}
                      className="category-card"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "8px 10px",
                        cursor: "pointer",
                        outline: selected === f.name ? "1px solid var(--accent, #7c3aed)" : "none",
                      }}
                      onClick={() => void openFile(f.name)}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.name}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        <small style={{ color: "var(--text-secondary)" }}>{fmtSize(f.size)}</small>
                        {isSessionFile(f.name) && (
                          <button
                            className="btn-secondary"
                            title="Open as session"
                            onClick={(e) => { e.stopPropagation(); void openAsSession(f.name); }}
                          >
                            ⇱
                          </button>
                        )}
                        <button
                          className="btn-secondary"
                          title="Download / share"
                          onClick={(e) => { e.stopPropagation(); void download(f.name); }}
                        >
                          ↓
                        </button>
                        <button
                          className="btn-secondary"
                          title="Delete"
                          onClick={(e) => { e.stopPropagation(); void del(f.name); }}
                        >
                          ✕
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3>{selected ? selected : "Preview"}</h3>
              {selected ? (
                <>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    spellCheck={false}
                    style={{
                      width: "100%",
                      height: 320,
                      fontFamily: "var(--font-mono, monospace)",
                      fontSize: "0.85em",
                      background: "var(--bg-secondary, #1e1e2e)",
                      color: "inherit",
                      border: "1px solid var(--border, #333)",
                      borderRadius: 6,
                      padding: 10,
                      resize: "vertical",
                    }}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button className="btn-primary" onClick={saveOpen}>Save</button>
                    <button className="btn-secondary" onClick={() => void download(selected)}>
                      Download
                    </button>
                  </div>
                </>
              ) : (
                <p style={{ color: "var(--text-secondary)", fontSize: "0.9em" }}>
                  Select a file to view or edit it. Files are shared by sending
                  the file itself (from your folder, or via Download here).
                </p>
              )}
            </div>
          </div>

          {busy && (
            <p style={{ color: "var(--text-secondary)", fontSize: "0.85em", marginTop: 8 }}>
              Working…
            </p>
          )}
        </>
      )}
    </div>
  );
}
